<template>
  <div class="image-upload-page" @paste="handlePaste">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <i class="el-icon-picture-outline header-icon"></i>
        <div>
          <h3>图片上传工具</h3>
          <p class="desc">上传图片到 CDN，快速获取图片链接</p>
        </div>
      </div>
    </div>

    <!-- Upload Section -->
    <div class="upload-section">
      <el-upload
        ref="upload"
        class="upload-area"
        drag
        action=""
        :http-request="onUpload"
        :show-file-list="false"
        :multiple="true"
        accept="image/*"
      >
        <div class="upload-content">
          <i class="el-icon-upload upload-icon"></i>
          <div class="upload-text">
            <p>将图片拖到此处，或<em>点击上传</em></p>
            <p class="upload-hint">支持 PNG、JPG、WebP 格式 · 可批量上传</p>
          </div>
          <div class="upload-shortcut">
            <i class="el-icon-document-copy"></i>
            <span>Ctrl/Cmd + V 粘贴上传</span>
          </div>
        </div>
      </el-upload>
    </div>

    <!-- Upload Progress Queue -->
    <div v-if="uploadQueue.length > 0" class="progress-section">
      <div class="section-header">
        <h4>上传中</h4>
        <span class="queue-count">{{ uploadQueue.length }} 个文件</span>
      </div>
      <div class="progress-list">
        <div v-for="(item, index) in uploadQueue" :key="index" class="progress-item">
          <div class="progress-info">
            <i class="el-icon-picture"></i>
            <span class="file-name">{{ item.name }}</span>
          </div>
          <div class="progress-bar-wrap">
            <el-progress
              :percentage="item.progress"
              :stroke-width="6"
              :show-text="false"
              color="#c04851"
            ></el-progress>
          </div>
        </div>
      </div>
    </div>

    <!-- Latest Result -->
    <div v-if="currentResult" class="result-section">
      <div class="section-header">
        <h4>
          <i class="el-icon-success success-icon"></i>
          上传成功
        </h4>
      </div>
      <div class="result-card">
        <div class="result-preview">
          <img :src="currentResult.url" class="preview-img" />
        </div>
        <div class="result-actions">
          <el-input
            v-model="currentResult.url"
            readonly
            size="small"
            class="url-input"
          >
            <template slot="prepend">
              <i class="el-icon-link"></i>
            </template>
          </el-input>
          <div class="action-buttons">
            <el-button
              :type="copySuccess ? 'success' : 'primary'"
              size="small"
              @click="copyUrl(currentResult.url)"
            >
              <i :class="copySuccess ? 'el-icon-check' : 'el-icon-document-copy'"></i>
              {{ copySuccess ? '已复制' : '复制链接' }}
            </el-button>
            <el-button
              type="info"
              size="small"
              plain
              @click="openUrl(currentResult.url)"
            >
              <i class="el-icon-view"></i>
              新窗口打开
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- History Section -->
    <div v-if="history.length > 0" class="history-section">
      <div class="section-header">
        <h4>历史记录</h4>
        <div class="header-actions">
          <span class="history-count">{{ history.length }} 张图片</span>
          <el-button
            type="text"
            size="mini"
            class="clear-btn"
            @click="confirmClearHistory"
          >
            <i class="el-icon-delete"></i>
            清空
          </el-button>
        </div>
      </div>
      <div class="history-grid">
        <div
          v-for="(item, index) in history"
          :key="index"
          class="history-item"
        >
          <div class="history-img-wrap">
            <img :src="item.url" class="history-img" />
            <div class="history-overlay">
              <el-button
                type="primary"
                size="mini"
                circle
                @click="copyUrl(item.url)"
              >
                <i class="el-icon-document-copy"></i>
              </el-button>
              <el-button
                type="danger"
                size="mini"
                circle
                @click="removeHistoryItem(index)"
              >
                <i class="el-icon-delete"></i>
              </el-button>
            </div>
          </div>
          <div class="history-info">
            <span class="history-time">{{ item.time }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!currentResult && history.length === 0 && uploadQueue.length === 0" class="empty-state">
      <i class="el-icon-picture-outline empty-icon"></i>
      <p>暂无上传记录</p>
      <p class="empty-hint">上传图片后会显示在这里</p>
    </div>
  </div>
</template>

<script>
import uploadMixin from "../../mixin/upload";

export default {
  name: "ImageUpload",
  mixins: [uploadMixin],
  data() {
    return {
      uploadQueue: [],
      currentResult: null,
      history: [],
      copySuccess: false
    };
  },
  methods: {
    onUpload(param) {
      const file = param.file;
      const queueItem = {
        name: file.name,
        progress: 0
      };
      this.uploadQueue.push(queueItem);

      // Simulate progress (since actual progress might not be available)
      const progressInterval = setInterval(() => {
        if (queueItem.progress < 90) {
          queueItem.progress += Math.random() * 20;
          if (queueItem.progress > 90) queueItem.progress = 90;
        }
      }, 200);

      this.uploadImg(param, {
        onSuccess: (data) => {
          clearInterval(progressInterval);
          queueItem.progress = 100;

          const url =
            "https://cdn.jirou.fit/" +
            data.uploadResult.ImageUri +
            "~tplv-700tmnc25s-yasuo.image";

          // Remove from queue after a short delay
          setTimeout(() => {
            const idx = this.uploadQueue.indexOf(queueItem);
            if (idx > -1) {
              this.uploadQueue.splice(idx, 1);
            }
          }, 500);

          // Update current result
          this.currentResult = {
            url,
            name: file.name
          };

          // Add to history
          this.history.unshift({
            url,
            name: file.name,
            time: this.formatTime(new Date())
          });

          this.$message.success("上传成功");
        },
        onError: () => {
          clearInterval(progressInterval);
          const idx = this.uploadQueue.indexOf(queueItem);
          if (idx > -1) {
            this.uploadQueue.splice(idx, 1);
          }
          this.$message.error("上传失败");
        }
      });
    },

    handlePaste(event) {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            this.onUpload({ file });
            this.$message.info("检测到剪贴板图片，开始上传...");
          }
          break;
        }
      }
    },

    copyUrl(url) {
      navigator.clipboard.writeText(url).then(() => {
        this.copySuccess = true;
        this.$message.success("已复制到剪贴板");
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      }).catch(() => {
        this.$message.error("复制失败");
      });
    },

    openUrl(url) {
      window.open(url, "_blank");
    },

    removeHistoryItem(index) {
      this.history.splice(index, 1);
      this.$message.success("已删除");
    },

    confirmClearHistory() {
      this.$confirm("确定要清空所有历史记录吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        this.history = [];
        this.currentResult = null;
        this.$message.success("已清空历史记录");
      }).catch(() => {});
    },

    formatTime(date) {
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    }
  }
};
</script>

<style scoped>
.image-upload-page {
  max-width: 800px;
  padding-bottom: 40px;
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.header-icon {
  font-size: 36px;
  color: var(--brand-primary, #c04851);
  margin-top: 4px;
}

.page-header h3 {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary, #1e293b);
}

.page-header .desc {
  margin: 0;
  color: var(--text-secondary, #64748b);
  font-size: 14px;
}

/* Section Styles */
.upload-section,
.progress-section,
.result-section,
.history-section {
  background: var(--bg-card, #fff);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: var(--shadow-light, 0 1px 3px rgba(0, 0, 0, 0.08));
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary, #1e293b);
  display: flex;
  align-items: center;
  gap: 8px;
}

.success-icon {
  color: #10b981;
}

/* Upload Area */
.upload-area {
  width: 100%;
}

.upload-area >>> .el-upload {
  width: 100%;
}

.upload-area >>> .el-upload-dragger {
  width: 100%;
  height: auto;
  min-height: 200px;
  border: 2px dashed var(--border-base, #e2e8f0);
  border-radius: 12px;
  background: var(--bg-table-header, #f8fafc);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area >>> .el-upload-dragger:hover {
  border-color: var(--brand-primary, #c04851);
  background: var(--brand-primary-lighter, #fef7f7);
}

.upload-content {
  text-align: center;
  padding: 24px;
}

.upload-icon {
  font-size: 48px;
  color: var(--brand-primary, #c04851);
  margin-bottom: 16px;
}

.upload-text p {
  margin: 0;
  color: var(--text-secondary, #64748b);
  font-size: 14px;
}

.upload-text p:first-child {
  font-size: 16px;
  color: var(--text-regular, #475569);
  margin-bottom: 4px;
}

.upload-text em {
  color: var(--brand-primary, #c04851);
  font-style: normal;
  cursor: pointer;
}

.upload-hint {
  font-size: 13px;
  color: var(--text-placeholder, #94a3b8);
}

.upload-shortcut {
  margin-top: 16px;
  padding: 8px 16px;
  background: var(--bg-table-hover, #f1f5f9);
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary, #64748b);
}

/* Progress Section */
.queue-count {
  font-size: 13px;
  color: var(--text-secondary, #64748b);
}

.progress-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.progress-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg-table-header, #f8fafc);
  border-radius: 8px;
}

.progress-info {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 150px;
}

.progress-info i {
  color: var(--brand-primary, #c04851);
  font-size: 18px;
}

.file-name {
  font-size: 13px;
  color: var(--text-regular, #475569);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
}

.progress-bar-wrap {
  flex: 1;
}

/* Result Section */
.result-card {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.result-preview {
  flex-shrink: 0;
}

.preview-img {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  object-fit: contain;
  background: var(--bg-table-header, #f8fafc);
  border: 1px solid var(--border-base, #e2e8f0);
}

.result-actions {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.url-input {
  width: 100%;
}

.url-input >>> .el-input-group__prepend {
  background: var(--bg-table-header, #f8fafc);
  border-color: var(--border-base, #e2e8f0);
  padding: 0 12px;
}

.url-input >>> .el-input__inner {
  font-size: 13px;
  font-family: monospace;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-buttons .el-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* History Section */
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.history-count {
  font-size: 13px;
  color: var(--text-placeholder, #94a3b8);
}

.clear-btn {
  color: #ef4444;
  padding: 4px 8px;
}

.clear-btn:hover {
  color: #dc2626;
}

.history-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
}

.history-item {
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg-table-header, #f8fafc);
  border: 1px solid var(--border-base, #e2e8f0);
  transition: all 0.2s ease;
}

.history-item:hover {
  box-shadow: var(--shadow-dark, 0 4px 12px rgba(0, 0, 0, 0.1));
  transform: translateY(-2px);
}

.history-img-wrap {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
}

.history-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.history-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.history-item:hover .history-overlay {
  opacity: 1;
}

.history-info {
  padding: 8px 12px;
}

.history-time {
  font-size: 12px;
  color: var(--text-placeholder, #94a3b8);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-placeholder, #94a3b8);
}

.empty-icon {
  font-size: 64px;
  color: var(--border-base, #e2e8f0);
  margin-bottom: 16px;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

.empty-hint {
  margin-top: 4px;
  font-size: 13px;
  color: var(--text-placeholder, #cbd5e1);
}

/* Button Colors */
.el-button--primary {
  background-color: var(--brand-primary, #c04851);
  border-color: var(--brand-primary, #c04851);
}

.el-button--primary:hover {
  background-color: var(--brand-primary-light, #d45a63);
  border-color: var(--brand-primary-light, #d45a63);
}

.el-button--success {
  background-color: #10b981;
  border-color: #10b981;
}

/* Responsive */
@media (max-width: 600px) {
  .result-card {
    flex-direction: column;
  }

  .preview-img {
    max-width: 100%;
  }

  .history-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
